<template>
  <div class="pages__create">
    <div class="">
      <v-row v-if="isLoading" class="mt-5" no-gutters>
        <v-col cols="12">
          <SkeletonLoaders type="card-heading, list-item-three-line" />
        </v-col>
      </v-row>
      <v-row v-else class="mt-5" no-gutters>
        <v-col cols="12" sm="12" xs="12" class="mb-5 ml-5">
          <redirect class="mb-5" title="Pages" route="website.pages" />
          <div class="page-header d-flex align-center">
            <h2 class="page-title mb-0">Update page</h2>
            <v-spacer></v-spacer>
            <v-btn @click="showAddLanguageDialog = true" color="success" outlined>
              <v-icon left>mdi-plus</v-icon>
              Add Language
            </v-btn>
          </div>
          <Notification type="error" :messages="errors" v-if="errors.length > 0" />
          <!-- <GroupSubmit :callbackCancel="onCancel" :callbackSubmit="onSubmit" /> -->
        </v-col>
        <v-col cols="12" class="px-5 mb-3">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2" size="28">mdi-web</v-icon>
            <h3 class="mb-0">Default Page</h3>
            <v-chip small color="primary" class="ml-2">Primary</v-chip>
          </div>
        </v-col>
        <v-col cols="12" md="8" sm="12" xs="12" class="px-5">
          <v-row no-gutters>
            <v-col cols="12" sm="12" xs="12" class="elevation-form pa-3">
              <h3>Page details</h3>
              <div class="mt-5">
                <InputText label="Title" :model="['pageData', 'title']" :validate="validate" />
              </div>
              <div class="mt-5">
                <TextEditor
                  v-if="pageData.bodyHtml"
                  :value="pageData.bodyHtml"
                  label="Content"
                  :callback="setContent"
                />
                <TextEditor label="Content" :callback="setContent" v-else />
              </div>
            </v-col>
            <v-col cols="12" sm="12" xs="12" class="elevation-form pa-3 mt-5">
              <template>
                <v-row justify="center">
                  <v-expansion-panels accordion>
                    <v-expansion-panel>
                      <v-expansion-panel-header disable-icon-rotate>
                        <div>
                          <h3>Search engine listing preview</h3>
                          <p class="mt-5">
                            Add a title and description to see how this collection might appear in a search engine
                            listing
                          </p>
                        </div>
                        <template v-slot:actions>
                          <span class="btn-link">Edit website SEO</span>
                        </template>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <template>
                          <div>
                            <span>Page title</span>
                            <v-text-field v-model="pageData.searchEngineTitle"></v-text-field>
                          </div>
                          <div>
                            <span>Meta description</span>
                            <v-textarea v-model="pageData.searchEngineMetaDescription"></v-textarea>
                          </div>
                          <div>
                            <span>URL and handle</span>
                            <v-text-field :prefix="getDomain()" v-model="pageData.handle"></v-text-field>
                          </div>
                        </template>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-row>
              </template>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="4" sm="12" xs="12">
          <v-row no-gutters>
            <v-col class="elevation-form pa-3" cols="12" xs="12">
              <h3>Visibility</h3>
              <v-radio-group v-model="pageData.published">
                <v-radio label="Visible (as of just now)" :value="true"></v-radio>
                <v-radio label="Hidden" :value="false"></v-radio>
              </v-radio-group>
            </v-col>
            <v-col class="elevation-form pa-3" cols="12" xs="12">
              <h3>Show title</h3>
              <v-radio-group v-model="pageData.isShowTitle">
                <v-radio label="Show Title" :value="true"></v-radio>
                <v-radio label="Hidden Title" :value="false"></v-radio>
              </v-radio-group>
            </v-col>
            <v-col class="elevation-form pa-3 mt-5" cols="12" xs="12">
              <h3>Template</h3>
              <p>Select a template for this page.</p>
              <InputText
                :model="['pageData', 'template']"
                itemText="name"
                itemValue="type"
                :itemsSelect="items"
                typeComponent="select"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12  px-5 my-3">
          <v-divider></v-divider>
        </v-col>
        <!-- Language Page Forms (Clone of Default) -->
        <template v-if="!isLoading && pageData.languages && pageData.languages.length > 0">
          <v-col cols="12" class="px-5 mt-5" v-for="(lang, index) in pageData.languages" :key="lang.lang">
            <!-- Language Header -->
            <div class="d-flex align-center mb-3">
              <v-icon color="success" class="mr-2" size="28">mdi-translate</v-icon>
              <h3 class="mb-0">{{ getLanguageLabel(lang.lang) }}</h3>
              <v-chip small color="success" outlined class="ml-2">{{ lang.lang.toUpperCase() }}</v-chip>
              <v-spacer></v-spacer>
              <v-btn icon color="error" @click="confirmDeleteLanguage(lang.lang, index)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>

            <v-divider class="mb-4"></v-divider>

            <!-- Language Page Content -->
            <v-row no-gutters>
              <v-col cols="12" md="8" sm="12" xs="12">
                <v-row no-gutters>
                  <v-col cols="12" sm="12" xs="12" class="elevation-form pa-3">
                    <h3>Page details</h3>
                    <div class="mt-5">
                      <span class="font-weight-medium">Title</span>
                      <v-text-field
                        v-model="lang.title"
                        placeholder="Page title for this language"
                        outlined
                        dense
                        :error-messages="!lang.title ? 'Title is required' : ''"
                      ></v-text-field>
                    </div>
                    <div class="mt-5">
                      <span class="font-weight-medium">Content</span>
                      <TextEditor
                        v-if="lang.bodyHtml"
                        :value="lang.bodyHtml"
                        label="Content"
                        :callback="content => setContentForLanguage(content, lang.lang)"
                      />
                      <TextEditor
                        label="Content"
                        :callback="content => setContentForLanguage(content, lang.lang)"
                        v-else
                      />
                    </div>
                  </v-col>
                  <v-col cols="12" sm="12" xs="12" class="elevation-form pa-3 mt-5">
                    <template>
                      <v-row justify="center">
                        <v-expansion-panels accordion>
                          <v-expansion-panel>
                            <v-expansion-panel-header disable-icon-rotate>
                              <div>
                                <h3>Search engine listing preview</h3>
                                <p class="mt-5">
                                  Add a title and description to see how this page might appear in a search engine
                                  listing
                                </p>
                              </div>
                              <template v-slot:actions>
                                <span class="btn-link">Edit website SEO</span>
                              </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <template>
                                <div>
                                  <span>Page title</span>
                                  <v-text-field v-model="lang.searchEngineTitle"></v-text-field>
                                </div>
                                <div>
                                  <span>Meta description</span>
                                  <v-textarea v-model="lang.searchEngineMetaDescription"></v-textarea>
                                </div>
                                <div>
                                  <span>URL and handle</span>
                                  <v-text-field :prefix="getDomain()" v-model="lang.handle"></v-text-field>
                                </div>
                              </template>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </v-row>
                    </template>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>
          </v-col>
        </template>
        <v-col cols="12" sm="12" xs="12" class="d-flex px-5">
          <v-btn @click="showDialogDelete = true" color="error" elevation="">Delete</v-btn>

          <v-spacer></v-spacer>
          <v-btn @click="$router.push({ name: 'website.pages' })" color="" class="mx-5">Cancel</v-btn>
          <v-btn @click="onSubmit" :loading="isLoadingAction" :disabled="isLoadingAction" color="primary">Save</v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Modal add language -->
    <v-dialog v-model="showAddLanguageDialog" max-width="500px">
      <v-card>
        <p style="font-size: 20px; padding-top: 14px; padding-left: 14px;">Add New Language</p>
        <v-card-text>
          <v-select
            v-model="newLanguageCode"
            :items="languageOptions"
            item-text="label"
            item-value="code"
            label="Select Language"
            outlined
            :error-messages="languageError"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelAddLanguage">Cancel</v-btn>
          <v-btn color="primary" :loading="isAddingLanguage" @click="addNewLanguage">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- <BeforeLeavePage /> -->
    <delete-action :content="'page ' + pageData.title" :title="'Delete ' + pageData.title" />
  </div>
</template>
<script>
import InputText from '@/views/website/components/inputs/InputDefault';
import TextEditor from '@/components/form/Wysiwyg';
import { required, minLength, maxLength, email } from 'vuelidate/lib/validators';
import Redirect from '@/components/RedirectTo';
import resourceBeforeLeavePage from '@/mixins/before-change-router';
import { pageService } from '@/apis/page.s';
import slugify from '@/helpers/slugify';
import Notification from '@/components/Notification';

export default {
  props: ['id'],
  components: {
    InputText,
    TextEditor,
    Redirect,
  },
  // mixins: [resourceBeforeLeavePage],
  validations: {
    pageData: {
      title: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(100),
      },
      searchEngineTitle: {
        required: false,
      },
      template: {
        required: false,
      },
    },
  },
  data() {
    return {
      showDialogDelete: false,
      pageData: {
        title: '',
        searchEngineTitle: '',
        bodyHtml: '',
        handle: '',
        searchEngineMetaDescription: '',
        template: 'page',
        published: true,
        isShowTitle: true,
        languages: [], // Multi-language support
      },
      items: [
        { type: 'page', name: 'Page' },
        { type: 'contact-page', name: 'Contact Page' },
        { type: 'review-page', name: 'Review Page' },
        { type: 'order-tracking-page', name: 'Order Tracking Page' },
      ],
      validate: [{ type: 'required' }, { type: 'maxLength', min: 3 }, { type: 'maxLength', max: 100 }],
      isLoadingAction: false,
      isLoading: false,
      errors: [],
      // Multi-language support
      showAddLanguageDialog: false,
      newLanguageCode: '',
      languageError: '',
      isAddingLanguage: false,
      languageOptions: [
        { code: 'en', label: 'English (EN)' },
        { code: 'us', label: 'English (US)' },
        { code: 'eu', label: 'European (EU)' },
        { code: 'es', label: 'Spanish (ES)' },
        { code: 'fr', label: 'French (FR)' },
        { code: 'de', label: 'German (DE)' },
        { code: 'it', label: 'Italian (IT)' },
        { code: 'pt', label: 'Portuguese (PT)' },
        { code: 'ja', label: 'Japanese (JA)' },
        { code: 'ko', label: 'Korean (KO)' },
        { code: 'zh', label: 'Chinese (ZH)' },
        { code: 'vi', label: 'Vietnamese (VI)' },
      ],
    };
  },
  created() {
    this.loadPage();
  },
  methods: {
    async submitDelete() {
      try {
        pageService.deleteById(this.id);
        this.$router.push({ name: 'website.pages' });
      } catch (error) {
        console.log(error);
      }
    },
    async loadPage() {
      this.isLoading = true;
      try {
        let pageData = await pageService.get(this.id);
        this.pageData = pageData.data;
        if (this.pageData.isShowTitle === undefined) {
          this.pageData.isShowTitle = false;
        }
        // Initialize languages array if not exists
        if (!this.pageData.languages) {
          this.pageData.languages = [];
        }
        this.isLoading = false;
      } catch (error) {
        this.errors = error;
        this.isLoading = false;
        console.log(error);
      }
    },
    setContent(content) {
      this.pageData.bodyHtml = content;
    },
    setContentForLanguage(content, langCode) {
      const langData = this.pageData.languages.find(lang => lang.lang === langCode);
      if (langData) {
        langData.bodyHtml = content;
      }
    },
    /*
      Get language label from code
    */
    getLanguageLabel(code) {
      const option = this.languageOptions.find(opt => opt.code === code);
      return option ? option.label : code.toUpperCase();
    },
    /*
      Cancel add language
    */
    cancelAddLanguage() {
      this.showAddLanguageDialog = false;
      this.newLanguageCode = '';
      this.languageError = '';
    },
    /*
      Add new language
    */
    async addNewLanguage() {
      if (!this.newLanguageCode) {
        this.languageError = 'Please select a language';
        return;
      }

      // Check if language already exists
      const exists = this.pageData.languages?.some(lang => lang.lang === this.newLanguageCode);
      if (exists) {
        this.languageError = 'This language already exists';
        return;
      }

      this.isAddingLanguage = true;

      try {
        // Clone current default page structure
        const newLangData = {
          lang: this.newLanguageCode,
          title: this.pageData.title,
          searchEngineTitle: this.pageData.searchEngineTitle,
          bodyHtml: this.pageData.bodyHtml,
          handle: this.pageData.handle,
          searchEngineMetaDescription: this.pageData.searchEngineMetaDescription,
          template: this.pageData.template,
          published: this.pageData.published,
          isShowTitle: this.pageData.isShowTitle,
        };

        if (!this.pageData.languages) {
          this.pageData.languages = [];
        }

        this.pageData.languages.push(newLangData);

        // Close dialog
        this.showAddLanguageDialog = false;
        this.newLanguageCode = '';
        this.languageError = '';

        this.$forceUpdate();
      } catch (error) {
        console.error('Error adding language:', error);
        this.languageError = 'Failed to add language';
      } finally {
        this.isAddingLanguage = false;
      }
    },
    /*
      Confirm delete language
    */
    confirmDeleteLanguage(langCode, index) {
      if (confirm(`Are you sure you want to delete ${this.getLanguageLabel(langCode)} version?`)) {
        this.deleteLanguage(index);
      }
    },
    /*
      Delete language
    */
    deleteLanguage(index) {
      this.pageData.languages.splice(index, 1);
      this.$forceUpdate();
    },
    async onSubmit() {
      this.$v.$touch();
      this.isLoadingAction = true;
      if (!this.$v.$invalid) {
        try {
          // Prepare page data with languages
          const pageDataToSave = {
            ...this.pageData,
            languages: this.pageData.languages || [],
          };

          let pageData = await pageService.update(pageDataToSave);
          if (pageData) {
            // this.$router.push({ name: 'website.pages.update', params: { id: pageData.data.id } });
          }
          this.isLoadingAction = false;
        } catch (error) {
          this.errors = error.response.data.message || [];
          this.isLoadingAction = false;
        }
      } else {
        this.isLoadingAction = false;
      }
    },
    onCancel() {},
    getDomain() {
      return `https://${this.$store.state.auth.accountInfo.storeDomain}/pages/`;
    },
  },
  watch: {
    'pageData.title': function() {
      this.pageData.handle = slugify(this.pageData.title);
      this.pageData.searchEngineTitle = this.pageData.title;
    },
    'pageData.bodyHtml': function() {
      this.pageData.searchEngineMetaDescription = this.pageData.bodyHtml;
    },
    'pageData.languages': {
      handler: function(languages) {
        // Update handle and searchEngineTitle for each language when title changes
        if (languages && languages.length > 0) {
          languages.forEach(lang => {
            if (lang.title && !lang.handle) {
              lang.handle = slugify(lang.title);
            }
            if (lang.title && !lang.searchEngineTitle) {
              lang.searchEngineTitle = lang.title;
            }
            if (lang.bodyHtml && !lang.searchEngineMetaDescription) {
              lang.searchEngineMetaDescription = lang.bodyHtml;
            }
          });
        }
      },
      deep: true,
    },
  },
};
</script>
<style lang="scss">
.pages__create {
  .btn-link {
    color: var(--v-primary-base);
    &:hover {
      text-decoration: none;
    }
  }
  .v-expansion-panel:before {
    box-shadow: none;
  }
  .v-expansion-panel-header__icon {
    position: absolute;
    right: 20px;
    top: 20px;
  }

  // Language Management Styles
  .font-weight-medium {
    font-weight: 500;
    font-size: 0.95rem;
    color: #555;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1976d2;
  }

  .elevation-form {
    background: white;
    border-radius: 8px;
  }
}
</style>
